import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "h-full !lg:overflow-visible overflow-x-auto bg-white !flex-1 min-w-[420px]" }
const _hoisted_2 = { class: "uppercase" }
const _hoisted_3 = { class: "uppercase" }
const _hoisted_4 = { class: "uppercase break-normal text-center" }
const _hoisted_5 = { class: "uppercase" }
const _hoisted_6 = { class: "uppercase" }
const _hoisted_7 = { class: "uppercase" }
const _hoisted_8 = ["title"]
const _hoisted_9 = ["title"]
const _hoisted_10 = { class: "flex justify-center mt-4" }
const _hoisted_11 = {
  key: 0,
  class: "pr-4"
}
const _hoisted_12 = { key: 1 }

import { uniq } from 'lodash';
import { computed, PropType, ref, Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { ITEM_PER_PAGE } from '@/services';
import { resume } from '@/plugins/resume';

import { useGetDeviceQuery } from '@/entities/device/hooks';
import { useGetOrganizationsQuery } from '@/entities/organization/hooks';
import { useGetInspectionQuery } from '@/entities/inspection/hooks';
import { useGetAnnotationSeveritiesQuery } from '@/entities/annotation-severity/hooks';
import { useGetAnnotationTypesQuery } from '@/entities/annotation-type/hooks';
import {
  useGetAnnotationsQuery,
} from '@/entities/annotation/hooks';
import { useGetUsersQuery } from '@/entities/user/hooks';

import { User } from '@/entities/user/types';
import { Annotation } from '@/entities/annotation/types';

import {
  NkTable,
  NkTableColumn,
  NkBadge,
  NkButton, NkInlineModal, NkGroup,
} from '@/core/components';
import { Device } from '@/entities/device/types';
import NkInspectionEditForm from '@/entities/inspection/components/form/InspectionEditForm.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'AnnotationTable',
  props: {
  pick: {
    type: Array as PropType<typeof annotationTableColumnSlugs[number][]>,
    default: () => [],
  },
  omit: {
    type: Array as PropType<typeof annotationTableColumnSlugs[number][]>,
    default: () => [],
  },
  annotationIds: {
    type: Array as PropType<Annotation['id'][]>,
    default: () => [],
  },
  rootDevice: {
    type: Object as PropType<Device>
  }
},
  emits: ['annotation-row-hover', 'annotation-row-click'],
  setup(__props, { emit }) {

const props = __props

const router = useRouter();

const page = ref(1);
const canFetchMore = ref(false);

const annotationTableColumnSlugs =
  ['problem-type', 'severity', 'hub-distance', 'history',
   'user', 'photos', 'comment', 'inspection', 'defect-size', 'serial-number',
    'blade-model', 'blade-id'] as const;



const route = useRoute();


// Fetching for annotations
const annotationIds = computed(() => props.annotationIds || []);
const hasAnnotationIds = computed(() => !!annotationIds.value.length);
const annotationQueryParams = computed(() => ({
  ids: [...annotationIds.value],
  page: page.value,
}));
const { data: annotations, refetch, isRefetching, isLoading } = useGetAnnotationsQuery({
  enabled: hasAnnotationIds,
  onSuccess: (data) => {
    canFetchMore.value = data.length >= ITEM_PER_PAGE;
  },
}, annotationQueryParams);

const usersQueryFilters: Ref = computed(() => ({ ids: uniq(annotations.value?.map(o => o.createdBy)?.filter(Boolean)) }));
const hasUsersQueryFilters = computed(() => !!usersQueryFilters.value.ids.length);
const { data: users } = useGetUsersQuery({ enabled: hasUsersQueryFilters }, usersQueryFilters);

// Fetching for annotationTypes
const annotationTypesIds = computed(() => annotations.value?.map(o => o.type) || []);
const hasAnnotationTypes = computed(() => !!annotationTypesIds.value.length);
const annotationTypesQueryParams = computed(() => ({
  ids: [...annotationTypesIds.value],
}));
const { data: annotationTypes } = useGetAnnotationTypesQuery({
  enabled: hasAnnotationTypes,
}, annotationTypesQueryParams);

const { data: organizations } = useGetOrganizationsQuery();

// Fetching for annotationSeverities
const annotationSeverityIds = computed(() => annotations.value?.map(o => o.severity) || []);
const hasSeverities = computed(() => !!annotationSeverityIds.value.length);
const severitiesQueryFilters = computed(() => ({
  ids: [...annotationSeverityIds.value],
}));
const { data: annotationSeverities } = useGetAnnotationSeveritiesQuery({
  enabled: hasSeverities,
}, severitiesQueryFilters);

const inspectionQueryData = computed(() => ({ id: Number(route.params.inspection) }));
const hasInspectionQueryData = computed(() => !!inspectionQueryData.value.id);
const { data: inspection } = useGetInspectionQuery(inspectionQueryData, { enabled: hasInspectionQueryData });

const deviceQueryData: Ref = computed(() => ({ id: inspection.value?.device }));
const hasDeviceQueryData = computed(() => !!deviceQueryData.value.id);
const { data: device } = useGetDeviceQuery(deviceQueryData, { enabled: hasDeviceQueryData });


const parentDeviceQueryData: Ref = computed(() => ({ id: device.value?.parent }));
const hasParentDeviceQueryData = computed(() => !!parentDeviceQueryData.value.id);
const { data: parentDevice } = useGetDeviceQuery(parentDeviceQueryData, { enabled: hasParentDeviceQueryData });

const getAnnotations = computed(() => annotations.value?.map(o => (
  {
  ...o,
  type: annotationTypes.value?.find(a => a.id === o.type),
  severity: annotationSeverities.value?.find(a => a.id === o.severity),
  deviceData: parentDevice,
  rootDevice: props.rootDevice
})));

const isDisplayed = (name: typeof annotationTableColumnSlugs[number]) => {
  if (!props.pick?.length && !props.omit?.length) {
    return true;
  }

  if (props.pick?.length) {
    return props.pick.includes(name);
  }

  if (props.omit?.length) {
    return !props.omit.includes(name);
  }

  return true;
};

const fetchMore = () => {
  if (!isLoading.value) {
    page.value += 1;
    refetch.value();
  }
};

const previousPage = () => {
  if (!isLoading.value) {
    page.value -= 1
    refetch.value()
  }
}

const getUserCreatedBy = (id: User['id']) => {
  const user = users.value?.find(o => o.id === id);
  const organization = organizations.value?.find(o => user?.organizations.includes(o.id));

  if (!id) {
    return '-';
  }

  return `${ organization?.name ? `${ organization?.name } : ` : '' } ${ user?.lastName && user?.firstName ? `${ user?.lastName } ${ user?.firstName }` : user?.email }`;
};

const onCellMouseEnter = ({ id }: Annotation) => {
  emit('annotation-row-hover', id);
};

const handleView = (data: Annotation) => {
  router.push({
    name: 'visionair-inspection',
    params: {
      inspection: data.inspection
    },
    query: {
      view: 2,
      annotation: data.id,
    }
  })
};

const handleEdit = (data: Annotation) => {
  router.push({
    name: 'visionair-inspection',
    params: {
      inspection: data.inspection
    },
    query: {
      view: 2,
      annotation: data.id,
      showMenu: 'open'
    }
  })
}

return (_ctx: any,_cache: any) => {
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _withDirectives((_openBlock(), _createBlock(_unref(NkTable), {
      clickable: "",
      data: _unref(getAnnotations),
      onCellMouseEnter: onCellMouseEnter,
      style: {"width":"100%"},
      "max-height": "575"
    }, {
      default: _withCtx(() => [
        (isDisplayed('blade-id'))
          ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
              key: 0,
              label: _ctx.$t('misc.blade_id'),
              width: "100"
            }, {
              default: _withCtx((scope) => [
                _createElementVNode("span", _hoisted_2, _toDisplayString(scope.row.deviceData?.id || '-'), 1)
              ]),
              _: 1
            }, 8, ["label"]))
          : _createCommentVNode("", true),
        (isDisplayed('inspection'))
          ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
              key: 1,
              label: "BLADE",
              width: "150"
            }, {
              default: _withCtx((scope) => [
                _createTextVNode(_toDisplayString(scope.row.parentDeviceName) + " - " + _toDisplayString(scope.row.deviceName), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (isDisplayed('annotation-id'))
          ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
              key: 2,
              label: _ctx.$t('misc.annotation_id'),
              width: "125"
            }, {
              default: _withCtx((scope) => [
                _createElementVNode("span", _hoisted_3, _toDisplayString(scope.row.id || '-'), 1)
              ]),
              _: 1
            }, 8, ["label"]))
          : _createCommentVNode("", true),
        (isDisplayed('problem-type'))
          ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
              key: 3,
              label: _ctx.$t('misc.problem_type'),
              width: "200"
            }, {
              default: _withCtx((scope) => [
                _createElementVNode("span", _hoisted_4, _toDisplayString(scope.row.type?.name || '-'), 1)
              ]),
              _: 1
            }, 8, ["label"]))
          : _createCommentVNode("", true),
        (isDisplayed('defect-size'))
          ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
              key: 4,
              label: _ctx.$t('misc.defect_size'),
              width: "150"
            }, {
              default: _withCtx((scope) => [
                _createElementVNode("span", _hoisted_5, _toDisplayString(scope.row.defectSize || '-'), 1)
              ]),
              _: 1
            }, 8, ["label"]))
          : _createCommentVNode("", true),
        (isDisplayed('serial-number'))
          ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
              key: 5,
              label: _ctx.$t('misc.serial_number'),
              width: "200"
            }, {
              default: _withCtx((scope) => [
                _createElementVNode("span", _hoisted_6, _toDisplayString(scope.row.rootDevice?.deviceValues.find(o => o.reference === 'serialNumber') ?
                scope.row.rootDevice?.deviceValues.find(o => o.reference === 'serialNumber').value :
                '-'), 1)
              ]),
              _: 1
            }, 8, ["label"]))
          : _createCommentVNode("", true),
        (isDisplayed('blade-model'))
          ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
              key: 6,
              label: _ctx.$t('misc.blade_model'),
              width: "150"
            }, {
              default: _withCtx((scope) => [
                _createElementVNode("span", _hoisted_7, _toDisplayString(scope.row.deviceData?.deviceValues.find(o => o.reference === 'model') ?
                  scope.row.deviceData?.deviceValues.find(o => o.reference === 'model').value :
                  '-'), 1)
              ]),
              _: 1
            }, 8, ["label"]))
          : _createCommentVNode("", true),
        (isDisplayed('hub-distance'))
          ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
              key: 7,
              label: 
          `${_ctx.$t('misc.hub_distance')} ${([3, 4, 5, 6].includes(Number(_unref(device)?.type)) ? _ctx.$t('misc.from_hub') : _ctx.$t('misc.from_ground'))}`
        ,
              width: "200"
            }, {
              default: _withCtx((scope) => [
                _createTextVNode(_toDisplayString(scope.row?.position?.hubHeight || '-'), 1)
              ]),
              _: 1
            }, 8, ["label"]))
          : _createCommentVNode("", true),
        (isDisplayed('severity'))
          ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
              key: 8,
              label: _ctx.$t('misc.severity'),
              width: "150"
            }, {
              default: _withCtx((scope) => [
                _createVNode(_unref(NkBadge), {
                  plain: "",
                  size: "sm",
                  color: scope.row.severity?.color
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(scope.row.severity?.name || '-'), 1)
                  ]),
                  _: 2
                }, 1032, ["color"])
              ]),
              _: 1
            }, 8, ["label"]))
          : _createCommentVNode("", true),
        (isDisplayed('user'))
          ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
              key: 9,
              label: _ctx.$t('misc.created_by'),
              width: "250"
            }, {
              default: _withCtx((scope) => [
                _createElementVNode("span", {
                  class: "line-clamp-1",
                  title: getUserCreatedBy(scope.row.createdBy)
                }, _toDisplayString(getUserCreatedBy(scope.row.createdBy) || '-'), 9, _hoisted_8)
              ]),
              _: 1
            }, 8, ["label"]))
          : _createCommentVNode("", true),
        (isDisplayed('comment'))
          ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
              key: 10,
              label: _ctx.$t('misc.comment')
            }, {
              default: _withCtx((scope) => [
                _createElementVNode("span", {
                  title: scope.row.comment
                }, _toDisplayString(_unref(resume)(scope.row.comment, 50) || '-'), 9, _hoisted_9)
              ]),
              _: 1
            }, 8, ["label"]))
          : _createCommentVNode("", true),
        _createVNode(_unref(NkTableColumn), {
          label: "Action",
          width: "70"
        }, {
          default: _withCtx((scope) => [
            _createVNode(_unref(NkButton), {
              size: "sm",
              plain: "",
              icon: "NkVisibility",
              onClick: ($event: any) => (handleEdit(scope.row))
            }, null, 8, ["onClick"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["data"])), [
      [_directive_loading, _unref(isLoading)]
    ]),
    _createElementVNode("div", _hoisted_10, [
      (canFetchMore.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
            _withDirectives((_openBlock(), _createBlock(_unref(NkButton), { onClick: fetchMore }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('actions.see_more')), 1)
              ]),
              _: 1
            })), [
              [_directive_loading, _unref(isRefetching)]
            ])
          ]))
        : _createCommentVNode("", true),
      (page.value > 1)
        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
            _withDirectives((_openBlock(), _createBlock(_unref(NkButton), { onClick: previousPage }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('actions.previous_page')), 1)
              ]),
              _: 1
            })), [
              [_directive_loading, _unref(isRefetching)]
            ])
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})